// A numeric input that DISPLAYS in the user's unit while its owner keeps
// canonical state (kcal, g, kg, ml). The buffer pattern from WeightStepper:
// typing edits a local string; each valid keystroke emits the converted
// canonical value; external canonical changes (an AI estimate landing, a
// barcode fill) re-render the converted display. Metric users get the
// identity conversion and never notice this component exists.
import { useEffect, useRef, useState } from "react";

interface Props {
  label: string;
  /** Canonical value as the owner stores it ("" = empty). */
  value: string;
  onChange: (canonical: string) => void;
  toDisplay: (canonical: number) => number;
  fromDisplay: (display: number) => number;
  /** Decimals kept on the CANONICAL value when emitting (0 = integer). */
  canonicalDecimals?: number;
  inputMode?: "numeric" | "decimal";
}

export function UnitInput({
  label, value, onChange, toDisplay, fromDisplay, canonicalDecimals = 0, inputMode = "decimal",
}: Props) {
  const fmt = (canonical: string): string => {
    const n = Number(canonical);
    return canonical.trim() === "" || !Number.isFinite(n) ? "" : String(toDisplay(n));
  };
  const [text, setText] = useState(fmt(value));
  const lastEmitted = useRef(value);

  useEffect(() => {
    // Only resync when the change came from OUTSIDE (estimate/product fill),
    // never for our own emission — that would fight the caret mid-typing.
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setText(fmt(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onText = (raw: string) => {
    setText(raw);
    const n = parseFloat(raw.replace(",", "."));
    if (raw.trim() === "" || !Number.isFinite(n)) {
      lastEmitted.current = "";
      onChange("");
      return;
    }
    const canonical = fromDisplay(n);
    const rounded = canonicalDecimals === 0
      ? String(Math.round(canonical))
      : String(Math.round(canonical * 10 ** canonicalDecimals) / 10 ** canonicalDecimals);
    lastEmitted.current = rounded;
    onChange(rounded);
  };

  return (
    <label className="block">
      <span className="text-sm text-fg-muted uppercase tracking-wider">{label}</span>
      <input
        type="text"
        inputMode={inputMode}
        value={text}
        onChange={(e) => onText(e.target.value)}
        className="input mt-1"
      />
    </label>
  );
}
